import React, { ReactNode, useCallback, useRef, useState } from 'react';
import styled from 'astroturf/react';
import classNames from 'classnames';
import {
  Popover,
  PopoverMode,
  PopoverWidthMode,
} from 'js/design_system/Popover';
import { Icon } from 'js/design_system/Icon';
import { useRandomId } from 'js/utils/hooks/useRandomId';
import { Errors } from 'js/design_system/forms/Errors';
import { usePopoverFocus } from 'js/design_system/forms/utils';

import 'app/assets/stylesheets/design_system/form.scss';

const DropdownMenuStyleContext = styled.div``;

const defaultRenderValue = (value: unknown) => {
  return value ? String(value) : '';
};

export type TypeaheadDropdownProps<T> = {
  /** Optional value to show as selected */
  value?: T | null;
  /** Options to show in the dropdown */
  options?: T[];
  /** The input's label. Shown at the top of the component. */
  label?: ReactNode;
  /** Accessible name for the input when no visible `label` is rendered. Ignored if `label` is set. */
  ariaLabel?: string;
  /** The placeholder for the underlying input */
  placeholder?: string;
  /** The errors to show beneath the input */
  errors?: ReactNode[];
  /** Whether to decorate the input with error styling */
  hasError?: boolean;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Get a key from a given option (for React) */
  keyExtractor?: (option: T, index: number) => React.Key;
  /** Render callback for a single option in the dropdown */
  renderOption: (option: T, index: number) => ReactNode;
  /** Callback to invoke when selecting an option */
  onChange: (option: T | null) => void;
  /** Render callback for the value */
  renderValue?: (value?: T | null) => string;
};

/**
 * # TypeaheadDropdown
 * A combination of Dropdown and Typeahead: click to open with all options visible,
 * then type to filter. Supports arbitrary renderable elements.
 *
 * Props:
 * - `value`: Optional value to show as selected
 * - `options`: Options to show in the dropdown
 * - `label`: The input's label. Shown at the top of the component.
 * - `ariaLabel`: Accessible name for the input when no visible `label` is rendered
 * - `placeholder`: The placeholder for the underlying input
 * - `errors`: The errors to show beneath the input
 * - `hasError`: Whether to decorate the input with error styling
 * - `disabled`: Whether the input is disabled
 * - `keyExtractor`: Get a key from a given option (for React)
 * - `renderOption`: Render callback for how to display a single option in the dropdown
 * - `onChange`: Callback to invoke when selecting an option
 * - `renderValue`: Render callback for the value
 *
 * ## Basic Usage
 *
 * @example
 *
 * ```tsx
 * import { TypeaheadDropdown } from 'js/design_system/forms/TypeaheadDropdown';
 *
 * const [value, setValue] = useState<string>();
 * const options = ['Apple', 'Banana', 'Cherry'];
 *
 * <TypeaheadDropdown<string>
 *   label="Fruit"
 *   value={value}
 *   options={options}
 *   placeholder="Select a fruit..."
 *   renderOption={(fruit) => fruit}
 *   onChange={setValue}
 * />
 * ```
 */
export const TypeaheadDropdown = <T,>({
  value,
  options = [],
  label,
  ariaLabel,
  placeholder,
  errors,
  hasError,
  disabled,
  keyExtractor = (option) => JSON.stringify(option),
  renderOption,
  onChange,
  renderValue = defaultRenderValue,
}: TypeaheadDropdownProps<T>) => {
  const id = useRandomId();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const filteredOptions = search
    ? options.filter((option) => {
        const optionText = renderValue(option).toLowerCase();
        const searchText = search.toLowerCase();
        return optionText.includes(searchText);
      })
    : options;

  const handleClose = useCallback(() => {
    setOpen(false);
    setSearch('');
  }, []);

  const handleSelect = useCallback(
    (option: T) => {
      onChange(option);
      handleClose();
      inputRef.current?.blur();
    },
    [onChange, handleClose],
  );

  usePopoverFocus(
    inputRef,
    useCallback(() => setOpen(true), []),
    handleClose,
  );

  const inputWrapperClasses = classNames('ds2-5-input--wrapper', {
    'ds2-5-input--error': !disabled && hasError,
    'ds2-5-input--disabled': disabled,
  });

  const labelClasses = label
    ? classNames('ds2-5-form--label', {
        'ds2-5-form--label-disabled': disabled,
      })
    : undefined;

  const displayValue = open ? search : renderValue(value);
  const showPlaceholder = !displayValue;

  return (
    <div className="ds2-5-input--container" aria-disabled={disabled}>
      {label && (
        <label
          className={labelClasses}
          htmlFor={`${id}-input`}
          id={`${id}-label`}
        >
          {label}
        </label>
      )}

      <Popover
        open={open && filteredOptions.length > 0}
        offset={4}
        mode={PopoverMode.Click}
        widthMode={PopoverWidthMode.Activator}
        closeOnClick={false}
        maxHeight={288}
        render={() => (
          <DropdownMenuStyleContext className="ds2-5-dropdown-menu">
            {filteredOptions.map((option, index) => {
              return (
                <button
                  className="ds2-5-dropdown-item"
                  key={keyExtractor(option, index)}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleSelect(option);
                  }}
                >
                  {renderOption(option, index)}
                </button>
              );
            })}
          </DropdownMenuStyleContext>
        )}
        onChange={!disabled ? setOpen : undefined}
      >
        <div className={inputWrapperClasses}>
          <input
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                handleClose();
                inputRef.current?.blur();
              }
            }}
            ref={inputRef}
            id={`${id}-input`}
            aria-labelledby={label ? `${id}-label` : undefined}
            aria-label={label ? undefined : ariaLabel}
            aria-errormessage={
              !disabled && hasError ? `${id}-errors` : undefined
            }
            aria-invalid={!disabled && hasError}
            type="text"
            placeholder={showPlaceholder ? placeholder : undefined}
            value={displayValue}
            disabled={disabled}
            onChange={(event) => setSearch(event.target.value)}
          />
          <Icon name="keyboard_arrow_down" size={20} />
        </div>
      </Popover>

      {!disabled && hasError && <Errors id={`${id}-errors`} errors={errors} />}
    </div>
  );
};
